'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuBanknote, LuCalendarDays, LuHash, LuMapPin } from 'react-icons/lu'
import { Box, Flex, Text } from '@chakra-ui/react'
import { Location } from '@leid/api-locations'
import { GetPriceResponse } from '@leid/api-orders'
import { format } from 'date-fns'
import { FormikTouched } from 'formik'

import { buildDate } from 'app/utils/dates'
import { ItemApi } from 'appTypes/items'
import { CartFormValues } from 'appTypes/orders'
import { DATE_FORMATS } from 'utils/constants'
import { getDateFormatLocale } from 'utils/getDateFormatLocale'
import { getOrderItemDescriptionText, getOrderItemPriceText } from 'utils/priceDescription'
import { removeDecimals } from 'utils/removeDecimals'

import { useIncludedFeesString } from './hooks/useIncludedFeesString'
import { RentalSectionHeaderIconWrapper, RentalSectionHeaderSpan, RentalSectionHeaderWrapper } from './styles'

type Props = {
  values: CartFormValues
  touched: FormikTouched<CartFormValues>
  isProlonging: boolean
  productPrice: GetPriceResponse | null
  productLocations: Location[]
  step: number
  product: ItemApi
  isRerent: boolean
}

const RentalSectionHeader = ({
  isProlonging,
  productPrice,
  values,
  touched,
  productLocations,
  step,
  product,
  isRerent,
}: Props): JSX.Element => {
  const { t } = useTranslation(['common'])

  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined)

  const rentedFrom = useMemo<Date | undefined>(() => {
    if (values.startDate && values.pickupAt) {
      return buildDate(values.startDate, values.pickupAt)
    }
  }, [values.startDate, values.pickupAt])

  const rentedTo = useMemo<Date | undefined>(() => {
    if (values.endDate && values.returnAt) {
      return buildDate(values.endDate, values.returnAt)
    }
  }, [values.endDate, values.returnAt])

  const shouldDisplayRentedTo = useMemo<boolean>(
    () => (isProlonging ? Boolean(touched.returnAt) : true),
    [isProlonging, touched.returnAt],
  )

  const formatDate = (date: Date) =>
    format(date, DATE_FORMATS.noYearDate, {
      locale: getDateFormatLocale(),
    })

  useEffect(() => {
    if (values.selectedLocationId) {
      const locations = productLocations.filter((s) => s.id === values.selectedLocationId)
      setSelectedLocation(locations.length > 0 ? locations[0] : undefined)
    } else {
      setSelectedLocation(undefined)
    }
  }, [values.selectedLocationId, productLocations])

  const includedFeesString = useIncludedFeesString(product, values, isProlonging, isRerent, productPrice)

  const getPriceSection = () => {
    if (productPrice?.price) {
      return (
        <>
          <Box as="div">
            {getOrderItemPriceText(productPrice.price, productPrice.rebateAmount, undefined, t, isRerent)}
            {includedFeesString && (
              <Text fontSize="xs" opacity={0.5} marginTop="-2px">
                {includedFeesString}
              </Text>
            )}
            {productPrice.rebateAmount !== undefined && (
              <Text fontSize="xs">
                {getOrderItemDescriptionText(
                  productPrice.rebateAmount,
                  productPrice.rebateCause,
                  productPrice.rawPrice,
                  undefined,
                  t,
                )}
              </Text>
            )}
          </Box>
        </>
      )
    }

    return (
      <Text fontSize="sm" opacity={0.5}>
        {removeDecimals(product.pricePerDay)} {t('nok')}/{t('day')}
      </Text>
    )
  }

  return (
    <RentalSectionHeaderWrapper>
      <Box paddingRight="40px">
        {step === 1 && <Text fontSize="lg">{t(isProlonging ? 'changeRentalPeriod' : 'selectRentalPeriod')}</Text>}
        {step === 2 && <Text fontSize="lg">{t('selectAdditionalOptions')}</Text>}
        {!isProlonging && (
          <Text fontSize="xs" fontWeight="bold" color="green.a200">
            {t('stepXofY', { current: step, maximum: 2 })}
          </Text>
        )}
      </Box>
      <Box padding="20px 20px 16px" border="2px solid {colors.green.a50}" borderRadius="sm">
        <Flex alignItems="center" marginBottom="5px">
          <RentalSectionHeaderIconWrapper>
            <LuBanknote size={12} />
          </RentalSectionHeaderIconWrapper>
          {getPriceSection()}
        </Flex>
        {productPrice?.deposit !== undefined && (
          <Flex alignItems="center" marginBottom="5px">
            <RentalSectionHeaderIconWrapper>
              <LuBanknote size={12} />
            </RentalSectionHeaderIconWrapper>
            <Text fontSize="sm">
              <RentalSectionHeaderSpan>{t('common:deposit')}: </RentalSectionHeaderSpan>
              {removeDecimals(productPrice.deposit)} {t('nok')}
            </Text>
          </Flex>
        )}
        {rentedFrom && (
          <Flex alignItems="center" marginBottom="5px">
            <RentalSectionHeaderIconWrapper>
              <LuCalendarDays size={12} />
            </RentalSectionHeaderIconWrapper>
            <Text fontSize="sm">
              <RentalSectionHeaderSpan>{t('common:pickup')}: </RentalSectionHeaderSpan>
              {formatDate(rentedFrom)}
            </Text>
          </Flex>
        )}
        {shouldDisplayRentedTo && rentedTo && (
          <Flex alignItems="center" marginBottom="5px">
            <RentalSectionHeaderIconWrapper>
              <LuCalendarDays size={12} />
            </RentalSectionHeaderIconWrapper>
            <Text fontSize="sm">
              <Box as="span" color="green.a200">
                {t('common:return')}:{' '}
              </Box>
              {formatDate(rentedTo)}
            </Text>
          </Flex>
        )}
        {(!rentedFrom || !rentedTo) && (
          <Flex alignItems="center" marginBottom="5px">
            <RentalSectionHeaderIconWrapper>
              <LuCalendarDays size={12} />
            </RentalSectionHeaderIconWrapper>
            <Text fontSize="sm" opacity={0.5}>
              {t('avRental')}: {product.averageRentalTime} {t('days')}
            </Text>
          </Flex>
        )}
        {product.allowsToRentManyAssets && (
          <Flex alignItems="center" marginBottom="5px">
            <RentalSectionHeaderIconWrapper>
              <LuHash size={12} />
            </RentalSectionHeaderIconWrapper>
            <Text fontSize="sm">
              <Box as="span" color="green.a200">
                {t('common:Amount')}:{' '}
              </Box>
              {values.amount}
            </Text>
          </Flex>
        )}
        {selectedLocation && (
          <Flex alignItems="center" marginBottom="5px">
            <RentalSectionHeaderIconWrapper>
              <LuMapPin size={12} />
            </RentalSectionHeaderIconWrapper>
            <Box>
              <Text fontSize="sm">{selectedLocation.name}</Text>
              <Text fontSize="sm" opacity={0.5}>
                {selectedLocation.address}
              </Text>
            </Box>
          </Flex>
        )}
      </Box>
    </RentalSectionHeaderWrapper>
  )
}

export default RentalSectionHeader
